// src/pages/StarRec/Blacklists/Requests.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as blacklistApi from "../../../api/blacklist/blacklistApi";

/* -------------------- UI HELPERS -------------------- */
function MessageBar({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="alert alert-info d-flex justify-content-between align-items-center">
      <div>{message}</div>
      <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div
      onMouseDown={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        zIndex: 9999,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="bg-white border rounded p-3"
        style={{ width: "min(900px, 100%)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0">{title}</h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="mb-3">
      <label className="form-label fw-semibold">{label}</label>
      <input className="form-control" value={value ?? ""} readOnly />
    </div>
  );
}

function safeText(v) {
  return (v ?? "").toString().trim();
}

/* -------------------- MAIN -------------------- */
const Requests = () => {
  const didLoadRef = useRef(false);

  const [activeTab, setActiveTab] = useState("businesses"); // "businesses" | "employees"
  const [data, setData] = useState({ businesses: [], employees: [] });

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [message, setMessage] = useState("");
  const showMessage = (txt) => {
    setMessage(txt);
    window.clearTimeout(window.__msgTimer);
    window.__msgTimer = window.setTimeout(() => setMessage(""), 2000);
  };

  // Filters
  const [filterCountry, setFilterCountry] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");

  // View
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  useEffect(() => {
    document.title = "StarRec Blacklist Requests";
  }, []);

  /* =========================================================
     ✅ NORMALIZERS
     - Ensures requestId ALWAYS exists (fixes requests//approve)
     - Supports snake_case from DB/Postman
  ========================================================== */

  const normalizeBusinessReq = (r) => {
    const reqId = (r?.requestId || r?.id || r?._id || "").toString();

    return {
      // ✅ Use requestId everywhere for approve/reject
      requestId: reqId,

      // extra ids
      entityId: (r?.entity_id || r?.entityId || r?.business_id || r?.businessId || "").toString(),
      userId: (r?.user_id || r?.userId || "").toString(),

      businessName: r?.business_name || r?.businessName || "",
      branchName: r?.branch_name || r?.branchName || "",

      country: r?.country || "",
      state: r?.state || "",
      city: r?.city || "",

      registrationNumber: r?.registration_number || r?.registrationNumber || "",
      categoryId: r?.category_id || r?.categoryId || "",

      reason: r?.reason || r?.blacklist_reason || r?.blacklistReason || "",
      createdAt: r?.created_at || r?.createdAt || "",

      // keep raw
      payload: r?.payload || {},
      raw: r,
    };
  };

  const normalizeEmployeeReq = (r) => {
    const reqId = (r?.requestId || r?.id || r?._id || "").toString();

    return {
      requestId: reqId,
      entityId: (r?.entity_id || r?.entityId || r?.employee_id || r?.employeeId || "").toString(),
      userId: (r?.user_id || r?.userId || "").toString(),

      fullName:
        r?.full_name ||
        r?.fullName ||
        [r?.firstName, r?.lastName].filter(Boolean).join(" ") ||
        "",

      starRecId: r?.starrec_id || r?.starRecId || r?.employeeId || "",
      taxId: r?.tax_id || r?.taxId || "",
      businessName: r?.business_name || r?.businessName || "",

      country: r?.country || "",
      state: r?.state || "",
      city: r?.city || "",

      position: r?.position || r?.role || "",
      personalEmail: r?.personal_email || r?.personalEmail || r?.email || "",
      mobileNumber: r?.mobile_number || r?.mobileNumber || r?.mobile || "",

      reason: r?.reason || r?.blacklist_reason || r?.blacklistReason || "",
      createdAt: r?.created_at || r?.createdAt || "",

      payload: r?.payload || {},
      raw: r,
    };
  };

  /* =========================================================
     ✅ LOAD REQUESTS (FINAL)
     - Maps server arrays and normalizes ids
  ========================================================== */
  const loadRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await blacklistApi.getRequests();

      const businessesRaw = Array.isArray(res?.businesses) ? res.businesses : [];
      const employeesRaw = Array.isArray(res?.employees) ? res.employees : [];

      setData({
        businesses: businessesRaw.map(normalizeBusinessReq),
        employees: employeesRaw.map(normalizeEmployeeReq),
      });
    } catch (e) {
      setError(e?.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = activeTab === "employees" ? data.employees : data.businesses;

  /* -------------------- FILTER DROPDOWNS -------------------- */
  const countries = useMemo(() => {
    return Array.from(new Set(rows.map((r) => safeText(r.country)).filter(Boolean))).sort();
  }, [rows]);

  const states = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .map((r) => safeText(r.state))
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry]);

  const cities = useMemo(() => {
    const list = rows
      .filter((r) => (filterCountry ? r.country === filterCountry : true))
      .filter((r) => (filterState ? r.state === filterState : true))
      .map((r) => safeText(r.city))
      .filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [rows, filterCountry, filterState]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterCountry && r.country !== filterCountry) return false;
      if (filterState && r.state !== filterState) return false;
      if (filterCity && r.city !== filterCity) return false;
      return true;
    });
  }, [rows, filterCountry, filterState, filterCity]);

  const selected = useMemo(() => {
    return (
      rows.find((r) => r.requestId === selectedRequestId) ||
      filteredRows.find((r) => r.requestId === selectedRequestId) ||
      null
    );
  }, [rows, filteredRows, selectedRequestId]);

  /* -------------------- VIEW -------------------- */
  const openView = (row) => {
    setSelectedRequestId(row.requestId);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewOpen(false);
    setSelectedRequestId(null);
  };

  /* -------------------- ACTIONS -------------------- */
  const approve = async (requestId) => {
    if (!requestId) return alert("Missing request id");
    setBusy(true);
    try {
      await blacklistApi.approveRequest(requestId);
      showMessage("Request approved successfully");
      closeView();
      await loadRequests();
    } catch (e) {
      alert(e?.message || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async (requestId) => {
    if (!requestId) return alert("Missing request id");
    setBusy(true);
    try {
      await blacklistApi.rejectRequest(requestId);
      showMessage("Request rejected successfully");
      closeView();
      await loadRequests();
    } catch (e) {
      alert(e?.message || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="fs-4 fw-bold m-0">Blacklist Requests</h1>
        <button className="btn btn-outline-primary" onClick={loadRequests} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <MessageBar message={message} onClose={() => setMessage("")} />

      {error ? (
        <div className="alert alert-danger d-flex justify-content-between align-items-center">
          <div>{error}</div>
          <button className="btn btn-sm btn-outline-danger" onClick={loadRequests}>
            Retry
          </button>
        </div>
      ) : null}

      {/* Tabs */}
      <div className="d-flex gap-2 mb-3">
        <button
          className={`btn ${activeTab === "businesses" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => {
            setActiveTab("businesses");
            setFilterCountry("");
            setFilterState("");
            setFilterCity("");
          }}
        >
          Businesses
        </button>
        <button
          className={`btn ${activeTab === "employees" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => {
            setActiveTab("employees");
            setFilterCountry("");
            setFilterState("");
            setFilterCity("");
          }}
        >
          Employees
        </button>
      </div>

      {/* Filters */}
      <div className="row g-3 mb-3">
        <div className="col-md-4">
          <label className="form-label">Country</label>
          <select
            className="form-select"
            value={filterCountry}
            onChange={(e) => {
              setFilterCountry(e.target.value);
              setFilterState("");
              setFilterCity("");
            }}
          >
            <option value="">All</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">State</label>
          <select
            className="form-select"
            value={filterState}
            onChange={(e) => {
              setFilterState(e.target.value);
              setFilterCity("");
            }}
            disabled={!filterCountry}
          >
            <option value="">All</option>
            {states.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="form-label">City</label>
          <select
            className="form-select"
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            disabled={!filterState}
          >
            <option value="">All</option>
            {cities.map((ct) => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-bordered table-striped align-middle">
          <thead>
            {activeTab === "businesses" ? (
              <tr>
                <th>Business Name</th>
                <th>Branch</th>
                <th>City</th>
                <th>State</th>
                <th>Country</th>
                <th style={{ width: 120 }} className="text-center">
                  Action
                </th>
              </tr>
            ) : (
              <tr>
                <th>Job Holder Name</th>
                <th>StarRec ID</th>
                <th>TAX ID</th>
                <th>Business Name</th>
                <th style={{ width: 120 }} className="text-center">
                  Action
                </th>
              </tr>
            )}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={activeTab === "businesses" ? 6 : 5} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={activeTab === "businesses" ? 6 : 5} className="text-center">
                  No requests found.
                </td>
              </tr>
            ) : activeTab === "businesses" ? (
              filteredRows.map((r) => (
                <tr key={r.requestId}>
                  <td>{r.businessName || "-"}</td>
                  <td>{r.branchName || "-"}</td>
                  <td>{r.city || "-"}</td>
                  <td>{r.state || "-"}</td>
                  <td>{r.country || "-"}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(r)}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              filteredRows.map((r) => (
                <tr key={r.requestId}>
                  <td>{r.fullName || "-"}</td>
                  <td>{r.starRecId || "-"}</td>
                  <td>{r.taxId || "-"}</td>
                  <td>{r.businessName || "-"}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(r)}>
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Modal */}
      <Modal
        open={viewOpen}
        onClose={closeView}
        title={activeTab === "businesses" ? "View Business Request" : "View Job Holder Request"}
      >
        {!selected ? (
          <div className="text-muted">No data.</div>
        ) : activeTab === "businesses" ? (
          <>
            <div className="row">
              <div className="col-md-6">
                <Field label="Business Name" value={selected.businessName} />
              </div>
              <div className="col-md-6">
                <Field label="Branch Name" value={selected.branchName} />
              </div>

              <div className="col-md-4">
                <Field label="Country" value={selected.country} />
              </div>
              <div className="col-md-4">
                <Field label="State" value={selected.state} />
              </div>
              <div className="col-md-4">
                <Field label="City" value={selected.city} />
              </div>

              <div className="col-md-6">
                <Field label="Registration Number" value={selected.registrationNumber} />
              </div>
              <div className="col-md-6">
                <Field label="Category ID" value={selected.categoryId} />
              </div>

              <div className="col-12">
                <Field label="Blacklist Reason" value={selected.reason} />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary" onClick={closeView} disabled={busy}>
                Back
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={() => reject(selected.requestId)}
                disabled={busy}
              >
                {busy ? "..." : "Reject"}
              </button>
              <button
                className="btn btn-outline-success"
                onClick={() => approve(selected.requestId)}
                disabled={busy}
              >
                {busy ? "..." : "Approve"}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="row">
              <div className="col-md-6">
                <Field label="Full Name" value={selected.fullName} />
              </div>
              <div className="col-md-6">
                <Field label="Position" value={selected.position} />
              </div>

              <div className="col-md-6">
                <Field label="StarRec ID" value={selected.starRecId} />
              </div>
              <div className="col-md-6">
                <Field label="TAX ID" value={selected.taxId} />
              </div>

              <div className="col-md-6">
                <Field label="Business Name" value={selected.businessName} />
              </div>
              <div className="col-md-6">
                <Field label="Mobile" value={selected.mobileNumber} />
              </div>

              <div className="col-md-6">
                <Field label="Email" value={selected.personalEmail} />
              </div>

              <div className="col-12">
                <Field label="Blacklist Reason" value={selected.reason} />
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary" onClick={closeView} disabled={busy}>
                Back
              </button>
              <button
                className="btn btn-outline-danger"
                onClick={() => reject(selected.requestId)}
                disabled={busy}
              >
                {busy ? "..." : "Reject"}
              </button>
              <button
                className="btn btn-outline-success"
                onClick={() => approve(selected.requestId)}
                disabled={busy}
              >
                {busy ? "..." : "Approve"}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Requests;
